import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';

/** Колокольчик: уведомления с сервера (создаёт `notify`), счётчик непрочитанных, «Прочитать все» сохраняется в БД.
 * Обновление — опрос раз в 60 с и при открытии (realtime/SSE — этап 1.7). */
type Kind = 'order' | 'reservation' | 'payment' | 'other';
type Filter = 'all' | Kind;

interface Notification {
  id: string;
  type: string;
  title: string;
  body: string | null;
  link: string | null;
  readAt: string | null;
  createdAt: string;
}

const kindOf = (type: string): Kind => (type === 'order' || type === 'reservation' || type === 'payment' ? type : 'other');

const kindStyles: Record<Kind, { icon: string; wrap: string }> = {
  order: { icon: 'ri-shopping-bag-3-line', wrap: 'bg-primary-50 text-primary-700' },
  reservation: { icon: 'ri-calendar-check-line', wrap: 'bg-accent-50 text-accent-700' },
  payment: { icon: 'ri-wallet-3-line', wrap: 'bg-secondary-100 text-secondary-800' },
  other: { icon: 'ri-information-line', wrap: 'bg-background-100 text-foreground-600' },
};

function relative(iso: string, t: (k: string, o?: Record<string, unknown>) => string): string {
  const diff = Math.max(0, Date.now() - new Date(iso).getTime());
  const min = Math.round(diff / 60000);
  if (min < 1) return t('portal.notif.time.justNow');
  if (min < 60) return t('portal.notif.time.minutes', { count: min });
  const h = Math.round(min / 60);
  if (h < 24) return t('portal.notif.time.hours', { count: h });
  return t('portal.notif.time.days', { count: Math.round(h / 24) });
}

export default function NotificationBell({ settingsPath = '/portal/team', allPath = '/portal/orders' }: { settingsPath?: string; allPath?: string }) {
  const { t } = useTranslation();
  const { me } = useAuth();
  const navigate = useNavigate();
  const boxRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState<Filter>('all');
  const [items, setItems] = useState<Notification[]>([]);
  const [unread, setUnread] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!me) return;
    setLoading(true);
    try {
      const res = await api.get<{ items: Notification[]; unread: number }>('/notifications');
      setItems(res.items);
      setUnread(res.unread);
      setError(null);
    } catch (e) {
      setError((e as ApiError).code);
    } finally {
      setLoading(false);
    }
  }, [me]);

  useEffect(() => {
    void load();
    const timer = window.setInterval(() => void load(), 60000);
    return () => window.clearInterval(timer);
  }, [load]);

  useEffect(() => {
    if (open) void load();
  }, [open, load]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const visible = filter === 'all' ? items : items.filter((item) => kindOf(item.type) === filter);

  const markAllRead = async () => {
    try {
      await api.post('/notifications/read-all');
      setItems((prev) => prev.map((n) => ({ ...n, readAt: n.readAt ?? new Date().toISOString() })));
      setUnread(0);
    } catch (e) {
      setError((e as ApiError).code);
    }
  };

  const openItem = async (item: Notification) => {
    setOpen(false);
    if (!item.readAt) {
      setItems((prev) => prev.map((n) => (n.id === item.id ? { ...n, readAt: new Date().toISOString() } : n)));
      setUnread((u) => Math.max(0, u - 1));
      api.post(`/notifications/${item.id}/read`).catch(() => undefined);
    }
    if (item.link) navigate(item.link);
  };

  const filters: { key: Filter; label: string; count: number }[] = [
    { key: 'all', label: t('portal.notif.filterAll'), count: items.length },
    { key: 'order', label: t('portal.notif.filterOrders'), count: items.filter((i) => kindOf(i.type) === 'order').length },
    { key: 'reservation', label: t('portal.notif.filterReservations'), count: items.filter((i) => kindOf(i.type) === 'reservation').length },
    { key: 'payment', label: t('portal.notif.filterPayments'), count: items.filter((i) => kindOf(i.type) === 'payment').length },
    { key: 'other', label: t('bell.filterOther'), count: items.filter((i) => kindOf(i.type) === 'other').length },
  ];

  return (
    <div className="relative" ref={boxRef}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-label={t('portal.shell.notifications')}
        aria-expanded={open}
        className={`relative flex h-8 w-8 cursor-pointer items-center justify-center rounded-md transition-colors ${
          open ? 'bg-background-100 text-foreground-900' : 'text-foreground-500 hover:bg-background-100 hover:text-foreground-900'
        }`}
      >
        <i className="ri-notification-3-line text-[16px]" />
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent-500 px-1 text-[9px] font-bold leading-none text-background-50 ring-2 ring-background-50">
            {unread}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed inset-x-3 top-[58px] z-50 overflow-hidden rounded-lg border border-background-200 bg-background-50 shadow-[0_18px_45px_-20px_rgba(15,23,42,0.35)] sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-2 sm:w-[392px]">
          <div className="flex items-start justify-between gap-3 border-b border-background-200 px-4 py-3">
            <div>
              <p className="text-[13px] font-semibold text-foreground-950">{t('portal.notif.title')}</p>
              <p className="mt-0.5 flex items-center gap-1.5 text-[10.5px] text-foreground-500">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary-400" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-primary-500" />
                </span>
                {unread > 0 ? t('portal.notif.unread', { count: unread }) : t('portal.notif.allRead')}
              </p>
            </div>
            <button
              type="button"
              onClick={markAllRead}
              disabled={unread === 0}
              className={`cursor-pointer whitespace-nowrap rounded border px-2 py-1 text-[10.5px] font-medium transition-colors ${
                unread === 0 ? 'cursor-default border-background-200 text-foreground-300' : 'border-background-200 text-foreground-600 hover:border-primary-200 hover:text-primary-700'
              }`}
            >
              {t('portal.notif.markAll')}
            </button>
          </div>

          <div className="flex items-center gap-1 overflow-x-auto border-b border-background-200 bg-background-100/60 px-2 py-1.5 thin-scroll">
            {filters.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => setFilter(item.key)}
                className={`flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-1 text-[10.5px] font-medium transition-colors ${
                  filter === item.key ? 'bg-background-50 text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-800'
                }`}
              >
                {item.label}
                <span className={filter === item.key ? 'text-primary-700' : 'text-foreground-400'}>{item.count}</span>
              </button>
            ))}
          </div>

          <div className="max-h-[336px] overflow-y-auto thin-scroll">
            {error ? (
              <div className="px-4 py-8 text-center">
                <p className="text-[12px] font-semibold text-foreground-800">{t('bell.loadError')}</p>
                <button type="button" onClick={() => void load()} className="mt-2 cursor-pointer text-[11px] font-semibold text-primary-700">
                  {t('ui.retry')}
                </button>
              </div>
            ) : visible.length === 0 ? (
              <div className="px-4 py-10 text-center">
                <i className={`${loading ? 'ri-loader-4-line animate-spin' : 'ri-notification-off-line'} text-[22px] text-foreground-300`} />
                <p className="mt-2 text-[12px] font-semibold text-foreground-800">{t('portal.notif.empty')}</p>
                <p className="mt-0.5 text-[11px] text-foreground-500">{t('portal.notif.emptyHint')}</p>
              </div>
            ) : (
              visible.map((item) => {
                const unreadItem = !item.readAt;
                const style = kindStyles[kindOf(item.type)];
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => void openItem(item)}
                    className={`flex w-full cursor-pointer items-start gap-2.5 border-b border-background-100 px-3.5 py-2.5 text-left transition-colors last:border-b-0 ${
                      unreadItem ? 'bg-primary-50/40 hover:bg-primary-50/70' : 'hover:bg-background-100'
                    }`}
                  >
                    <span className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-[14px] ${style.wrap}`}>
                      <i className={style.icon} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-start gap-2">
                        <span className={`min-w-0 flex-1 text-[11.5px] leading-4 ${unreadItem ? 'font-semibold text-foreground-950' : 'font-medium text-foreground-700'}`}>{item.title}</span>
                        {unreadItem && <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-accent-500" />}
                      </span>
                      {item.body && <span className="mt-0.5 block truncate text-[10.5px] leading-4 text-foreground-500">{item.body}</span>}
                      <span className="mt-1 block text-[9.5px] font-medium uppercase tracking-wide text-foreground-400">{relative(item.createdAt, t)}</span>
                    </span>
                  </button>
                );
              })
            )}
          </div>

          <div className="flex items-center justify-between border-t border-background-200 bg-background-100/60 px-3.5 py-2">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                navigate(settingsPath);
              }}
              className="flex cursor-pointer items-center gap-1 text-[10.5px] font-medium text-foreground-500 transition-colors hover:text-primary-700"
            >
              <i className="ri-settings-3-line text-[12px]" />
              {t('portal.notif.settings')}
            </button>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                navigate(allPath);
              }}
              className="flex cursor-pointer items-center gap-1 text-[10.5px] font-semibold text-primary-700 transition-colors hover:text-primary-800"
            >
              {t('portal.notif.showAll')}
              <i className="ri-arrow-right-s-line text-[13px]" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
